import { Component, OnInit } from '@angular/core';
import { Validators, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { iif, Observable, of } from 'rxjs';
import { tap, filter, debounceTime, takeUntil, take, switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import * as DecoupledEditor from '@ckeditor/ckeditor5-build-decoupled-document';
import { TranslateService } from '@ngx-translate/core';

import * as fromIts2Store from '@apps/its2/store';
import * as fromRootStore from '@apps/root/store';
import { CreateService, Step2, Answer } from '@apps/its2/models/create-service';
import { IAuroraState } from '@apps/aurora.state';
import { CreateServiceBase } from '../common/create-service-base';
import { AppConstants } from '@apps/app.constants';
import { CatalogEntryQuestion } from '@apps/its2/domain/models';
import { NextParams, StepState } from '@ui/misc/wizard/models';
import { Its2EndpointGuid } from '@apps/bckp/bckp.constants';

@Component({
    selector: 'aa-create-service-step2',
    templateUrl: './create-service-step2.component.html',
    styleUrls: [
        './create-service-step2.component.scss',
        '../../request-create.scss'
    ],
    standalone: false
})
export class CreateServiceStep2Component extends CreateServiceBase implements OnInit
{
    private static stepIdx: number = 1;
    public catalogEntryQuestions: CatalogEntryQuestion[] = null;
    public isProcessing: boolean = false;
    public stepForm: UntypedFormGroup;
    public editor = DecoupledEditor;
    public configEditor = AppConstants.configEditor;
    public step2: Step2;
    public its2EndpointGuid: any = Its2EndpointGuid;

    constructor (
        translateService: TranslateService,
        private store: Store<IAuroraState>,
        private actions: Actions
    )
    {
        super(translateService);

        this.step2 = new Step2(translateService.currentLang);
        // this.store.dispatch(
        //     fromRootStore.RootMiscStoreActions.RootWizardStepState({
        //         stepIdx: CreateServiceStep2Component.stepIdx,
        //         valid: false
        //     })
        // );
        this.store.select(fromIts2Store.getCreateService)
            .pipe(
                filter((cs: CreateService): cs is CreateService => !!cs?.step1?.catalogEntry),
                take(1),
                tap((cs: CreateService) =>
                {
                    const catalogGuid = cs.step1.catalogEntry.catalogGuid;

                    this.step2 = new Step2(translateService.currentLang, cs.step2);
                    this.store
                        .select(fromIts2Store.getIts2CatalogsServiceQuestions, { catalogGuid })
                        .pipe(
                            filter((ceqs: CatalogEntryQuestion[]): ceqs is CatalogEntryQuestion[] => ceqs !== null && ceqs !== undefined),
                            take(1),
                            tap((ceqs: CatalogEntryQuestion[]) =>
                            {
                                this.catalogEntryQuestions = ceqs;
                                this.createForm();

                                const isStepSkipped = ceqs.length === 0;
                                this.store.dispatch(
                                    fromIts2Store.Its2MiscActions.Its2RequestsServiceCreateSetSkipped({
                                        idx: 2,
                                        isSkipped: isStepSkipped
                                    })
                                );
                                this.store.dispatch(
                                    fromRootStore.RootMiscStoreActions.RootWizard2StepEnabled({
                                        stepIdx: CreateServiceStep2Component.stepIdx,
                                        deactivated: isStepSkipped
                                    })
                                );
                                this.store.dispatch(
                                    fromRootStore.RootMiscStoreActions.RootWizard2StepState({
                                        stepIdx: CreateServiceStep2Component.stepIdx,
                                        valid: isStepSkipped
                                    })
                                );
                            })
                        )
                        .subscribe();
                })
            )
            .subscribe();
    }

    ngOnInit ()
    {
        this.actions
            .pipe(
                ofType(fromRootStore.RootMiscStoreActions.RootWizard2StepExitRequested),
                filter((nextParams: NextParams) =>
                    nextParams.oldStepIdx === CreateServiceStep2Component.stepIdx
                ),
                switchMap((nextParams: NextParams) =>
                    this.onSubmit()
                        .pipe(
                            switchMap((isValid: boolean) =>
                                iif(
                                    () => isValid,
                                    of(true)
                                        .pipe(
                                            tap(() =>
                                            {
                                                this.store.dispatch(
                                                    fromIts2Store.Its2MiscActions.Its2RequestsServiceCreateStep2Exited({ step2: this.step2 })
                                                );
                                                this.store.dispatch(
                                                    fromRootStore.RootMiscStoreActions.RootWizard2StepState(
                                                        new StepState(CreateServiceStep2Component.stepIdx, true)
                                                    )
                                                );
                                                this.store.dispatch(
                                                    fromRootStore.RootMiscStoreActions.RootWizard2StepExitAccepted(nextParams)
                                                );
                                            })
                                        ),
                                    of(false)
                                        .pipe(
                                            tap(() =>
                                            {
                                                this.store.dispatch(
                                                    fromRootStore.RootMiscStoreActions.RootWizard2StepState(
                                                        new StepState(CreateServiceStep2Component.stepIdx, false)
                                                    )
                                                );
                                                this.store.dispatch(
                                                    fromRootStore.RootMiscStoreActions.RootWizard2StepExitRefused(nextParams)
                                                );
                                            })
                                        )
                                )
                            )
                        )
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    onSubmit = (): Observable<boolean> =>
    {
        this.markControlsDirty(this.stepForm);
        if (this.stepForm.invalid)
        {
            return of(false);
        }

        this.step2.valid = true;
        this.step2.answers = this.catalogEntryQuestions
            .map((ceq: CatalogEntryQuestion) =>
                new Answer(ceq, this.stepForm.get(ceq.name).value)
            );

        return of(true);
    };

    private createForm ()
    {
        this.stepForm = new UntypedFormGroup({});
        this.addFields(this.stepForm, this.catalogEntryQuestions, this.step2);
        this.formListener();
    }

    private addFields (fg: UntypedFormGroup, ceqs: CatalogEntryQuestion[], step2: Step2): void
    {
        // debugger;
        ceqs.forEach((ceq: CatalogEntryQuestion) =>
        {
            if (!ceq.hidden && ceq.required)
            {
                ceq.validators.push(Validators.required);
            }

            if (step2?.answers?.length !== 0)
            {
                const answer = step2.answers.find((a: Answer) =>
                    a.question.name === ceq.name
                );
                if (answer !== undefined)
                {
                    fg.addControl(ceq.name, new UntypedFormControl(answer.response, ceq.validators));
                }
                else
                {
                    fg.addControl(ceq.name, new UntypedFormControl('', ceq.validators));
                }
            }
            else
            {
                fg.addControl(ceq.name, new UntypedFormControl('', ceq.validators));
            }
        });
    }

    private formListener ()
    {
        this.stepForm.valueChanges
            .pipe(
                debounceTime(250),
                tap(() =>
                    this.store.dispatch(
                        fromRootStore.RootMiscStoreActions.RootDirtyPopinFormSet({ dirty: true })
                    )
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();

        this.stepForm.statusChanges
            .pipe(
                debounceTime(250),
                tap((result: string) =>
                {
                    if (result === 'VALID')
                    {
                        this.store.dispatch(
                            fromRootStore.RootMiscStoreActions.RootWizardStepState({
                                stepIdx: CreateServiceStep2Component.stepIdx,
                                valid: true
                            })
                        );
                    }
                    else
                    {
                        // INVALID, PENDING, or DISABLED
                        this.store.dispatch(
                            fromRootStore.RootMiscStoreActions.RootWizardStepState({
                                stepIdx: CreateServiceStep2Component.stepIdx,
                                valid: false
                            })
                        );
                    }
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }
}
